import type { User } from "../slices/authSlice"
import CurrencySelector from "../components/layout/CurrencySelector"
import { useEffect, useState } from "react"
import { SpinnerText } from "../components/ui/Spinner"
import { useParams, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import AppLayout from "../layout/AppLayout"
import GoodsSection from "../components/trademark/goods_services/GoodsSection"
import SignatureSection from "../components/trademark/signature/SignatureSection"
import ActionSection from "../components/trademark/action/ActionSection"
import FeesSection from "../components/trademark/fees/FeesSection"
import ServiceDescriptions from "../components/ui/ServiceDescriptions"
import { FilingSidebarAccordion } from "../components/ui/FilingSidebarAccordion"
import MarkSection, { MarkData } from "../components/trademark/mark/MarkSection"
import ProgressSection from "../components/trademark/progress/ProgressSection"
import ReferenceNumberSection from "../components/trademark/reference_number/ReferenceNumberSection"
import ApplicantAndRepresentativeWrapper from "../components/trademark/applicant/ApplicantAndRepresentativeWrapper"
import ClaimsSection from "../components/trademark/claims/ClaimsSection"
import StatusToast from "../components/ui/StatusToast"
import SectionFieldError from "../components/ui/SectionFieldError"
import { getApplicationById, updateApplicationStatus } from "../services/applicationService"
import {
  ApiFieldError,
  formatApiError,
  getApiFieldErrors
} from "../utils/formatApiError"
import { RootState } from "../store/store"


function getEntityId(value: any) {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object") return String(value._id || value.id || "")
  return String(value)
}


export default function TrademarkFormPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const location = useLocation()
  const isViewMode = location.pathname.includes("/view")
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const [loading, setLoading] = useState(isEdit)

  const [applicationStatus, setApplicationStatus] = useState<"DRAFT" | "VALIDATED" | "AWAITING_RELEASE" | "PAYMENT_PENDING" | "PAID" | "ERROR">("DRAFT")
  const isSubmitted =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE"
  const isPaid = isViewMode || (!isAdmin && isSubmitted)

  const [referenceNumber, setReferenceNumber] = useState("")
  const [markData, setMarkData] = useState<MarkData | null>(null)
  const [markType, setMarkType] = useState<MarkData["type"] | null>(null)
  const [goodsAndServices, setGoodsAndServices] = useState<any[] | null>(null)
  const [feeGoodsAndServices, setFeeGoodsAndServices] = useState<any[] | null>(null)
  const [applicants, setApplicants] = useState<any[] | null>(null)
  const [priorityData, setPriorityData] = useState<any[] | null>(null)
  const [seniorityData, setSeniorityData] = useState<any[] | null>(null)
  const [signature, setSignature] = useState<any | null>(null)
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id || "")
  const [ownerUserId, setOwnerUserId] = useState("")

  const [orderingParty, setOrderingParty] = useState<Partial<User> | null | undefined>(undefined)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [markId, setMarkId] = useState<string | null>(null)
  const [goodsAndServicesId, setGoodsAndServicesId] = useState<string | null>(null)
  const [signatureId, setSignatureId] = useState<string | null>(null)
  const [formError, setFormError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<ApiFieldError[]>([])

  useEffect(() => {
    if (!isEdit) return

    async function load() {
      try {
        const app = await getApplicationById(id!)
        setOrderingParty(typeof app.userId === "object" ? app.userId : null)
        setDraftId(app._id)
        setOwnerUserId(getEntityId(app.userId) || currentUserId)
        setApplicationStatus(app.status || "DRAFT")
        setReferenceNumber(app.referenceNumber?.referenceNumber || "")

        if (app.markId) {
          setMarkId(app.markId._id)
          setMarkData({
            type: app.markId.type,
            word: app.markId.word || "",
            text: app.markId.text || "",
            colorClaim: app.markId.colorClaim || "",
            category: app.markId.category,
            imageFile: null,
            constitutionFile: null,
            euipoDocumentId: app.markId.euipoDocumentId,
            euipoConstitutionDocumentId: app.markId.euipoConstitutionDocumentId,
            imageData: app.markId.imageData,
            contentType: app.markId.contentType
          })
        }

        if (app.goodsAndServices) {
          setGoodsAndServicesId(app.goodsAndServices._id)
          setGoodsAndServices(
            app.goodsAndServices.entries.flatMap((e: any) =>
              e.terms.map((t: string) => ({
                classNumber: e.classNumber,
                text: t
              }))
            )
          )
          setFeeGoodsAndServices(
            app.goodsAndServices.entries.flatMap((e: any) =>
              e.terms.map((t: string) => ({
                classNumber: e.classNumber,
                text: t
              }))
            )
          )
        }

        setApplicants(app.applicants || [])
        setPriorityData(app.priorities || [])
        setSeniorityData(app.seniorities || [])

        if (app.signature) {
          setSignatureId(app.signature._id)
          setSignature({ ...app.signature, accepted: true })
        }
      } catch (error) {
        setFormError(
          formatApiError(
            error,
            "The trademark application could not be loaded. Please reload the page and try again."
          )
        )
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id, isEdit, currentUserId])

  const hasSelectedClass = (feeGoodsAndServices || []).some((entry) => {
    const classNumber = Number(entry?.classNumber)
    return Number.isInteger(classNumber) && classNumber > 0
  })
  const priceReady = !!markType && hasSelectedClass

  const paymentEnabled =
    !!referenceNumber.trim() &&
    !!markData &&
    !!goodsAndServices?.length &&
    !!applicants?.length &&
    !!signature?.accepted
  const statusMustStayLocked =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE" ||
    applicationStatus === "ERROR"

  useEffect(() => {
    if (!draftId || isPaid || statusMustStayLocked) return

    if (!paymentEnabled) {
      if (applicationStatus !== "DRAFT") {
        updateApplicationStatus(draftId, "DRAFT").catch(() => {})
        setApplicationStatus("DRAFT")
      }
    }
  }, [paymentEnabled, applicationStatus, draftId, isPaid, statusMustStayLocked])

  const sectionErrors = (...fields: string[]) =>
    fieldErrors
      .filter(error =>
        fields.some(field =>
          error.field === field || error.field.startsWith(`${field}.`)
        )
      )
      .map(error => error.message)

  const handleGoodsAndServicesChange = (value: any[] | null) => {
    setGoodsAndServices(value)

    if (value?.length) {
      setFieldErrors(prev =>
        prev.filter(error =>
          error.field !== "goodsAndServices" &&
          !error.field.startsWith("goodsAndServices.")
        )
      )
    }
  }

  if (loading) {
    return (
      <AppLayout title="Trademark Application" wide>
        <div className="flex justify-center py-32 text-gray-600">
          <SpinnerText>Loading application...</SpinnerText>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Trademark Application" wide>
      {formError && (
        <StatusToast
          type="error"
          message={formError}
          onClose={() => setFormError("")}
        />
      )}

      <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <ProgressSection
            complete={{
              trademark: !!markData,
              goods: !!goodsAndServices?.length,
              applicant: !!applicants?.length,
              signature: !!signature?.accepted,
              payment: isSubmitted
            }}
          />
        </div>
      </div>

      <div className="flex justify-center py-12">
        <div className="grid grid-cols-1 gap-7 max-w-7xl w-full xl:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="flex flex-col gap-10 p-5 sm:p-8 xl:p-12 bg-white border rounded-2xl shadow-sm">
            <ReferenceNumberSection
              value={referenceNumber}
              onChange={setReferenceNumber}
              disabled={isPaid}
            />

            <div id="trademark">
              <SectionFieldError messages={sectionErrors("mark")} />
              <MarkSection
                disabled={isPaid}
                onChange={setMarkData}
                onTypeChange={setMarkType}
                initialData={markData}
              />
            </div>

            <div id="goods">
              <SectionFieldError messages={sectionErrors("goodsAndServices")} />
              <GoodsSection
                disabled={isPaid}
                goodsAndServicesId={goodsAndServicesId}
                onChange={handleGoodsAndServicesChange}
                onFeesChange={setFeeGoodsAndServices}
                onIdChange={setGoodsAndServicesId}
                initialData={goodsAndServices}
                currentApplicationId={draftId}
                ownerUserId={ownerUserId || currentUserId}
              />
            </div>

            <div id="applicant">
              <SectionFieldError messages={sectionErrors("applicants")} />
              <ApplicantAndRepresentativeWrapper
                orderingParty={orderingParty}
                disabled={isPaid}
                onApplicantChange={setApplicants}
                initialData={applicants ?? []}
                importSource="trademark"
                currentApplicationId={draftId}
                ownerUserId={ownerUserId || currentUserId}
              />
            </div>

            <div id="claims">
              <ClaimsSection
                disabled={isPaid}
                onPriorityChange={setPriorityData}
                onSeniorityChange={setSeniorityData}
                initialPriorities={priorityData ?? []}
                initialSeniorities={seniorityData ?? []}
              />
            </div>

            <div id="signature">
              <SectionFieldError messages={sectionErrors("signature")} />
              <SignatureSection
                disabled={isPaid}
                onChange={setSignature}
                initialData={signature}
              />
            </div>

          </div>

          <div id="payment" className="self-start scroll-mt-28 overflow-y-visible xl:sticky xl:top-[128px] xl:max-h-[calc(100vh-9rem)] xl:overflow-y-auto xl:[scrollbar-width:none] xl:[&::-webkit-scrollbar]:hidden">
            <ActionSection
              applicationStatus={applicationStatus}
              referenceNumber={referenceNumber}
              markData={markData}
              goodsAndServices={goodsAndServices}
              applicants={applicants}
              priorityData={priorityData}
              seniorityData={seniorityData}
              signature={signature}
              signatureId={signatureId}
              paymentEnabled={paymentEnabled && !isPaid}
              draftId={draftId}
              markId={markId}
              goodsAndServicesId={goodsAndServicesId}
              onDraftSaved={setDraftId}
              onMarkSaved={setMarkId}
              onSignatureSaved={setSignatureId}
              onGoodsAndServicesSaved={setGoodsAndServicesId}
              onValidationError={error => setFieldErrors(getApiFieldErrors(error))}
            />
            <FilingSidebarAccordion>
              <FeesSection
                goodsAndServices={feeGoodsAndServices}
                priorityData={priorityData}
                seniorityData={seniorityData}
                ready={priceReady}
              />
              <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                <CurrencySelector />
              </div>
              <ServiceDescriptions type="trademark" />
            </FilingSidebarAccordion>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
